import { Component, Input, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthSearchService } from '../../features/growth/growth-search.service';

interface SeoProfileOption {
  id: string;
  label: string;
}

@Component({
  standalone: true,
  selector: 'ain-growth-seo-profile-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GrowthSeoProfileSelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="id"
      [placeholder]="t('growth.selectors.seoProfile.placeholder')"
      [showClear]="showClear"
      [filter]="true"
      filterBy="label,id"
      [disabled]="disabled || loading()"
      [loading]="loading()"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`.selector { width: 100%; }`]
})
export class GrowthSeoProfileSelectorComponent implements ControlValueAccessor {
  private readonly searchService = inject(GrowthSearchService);
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() showClear = true;
  @Input() appendTo: 'body' | 'self' = 'body';

  readonly loading = signal(false);
  readonly options = signal<SeoProfileOption[]>([]);
  readonly value = signal<string | null>(null);
  disabled = false;
  private pendingValue: string | null = null;

  constructor() {
    this.load();
  }

  writeValue(value: string | null): void {
    this.pendingValue = value?.trim() || null;
    this.value.set(this.pendingValue);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onValueChange(value: string | null): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private load(): void {
    this.loading.set(true);
    this.searchService.listSeoProfiles().subscribe({
      next: rows => {
        this.options.set(rows.map(row => ({ id: row.id, label: `${row.title} (${row.targetType})` })));
        if (this.pendingValue)
          this.value.set(this.pendingValue);
        this.loading.set(false);
      },
      error: () => {
        this.options.set([]);
        this.loading.set(false);
      }
    });
  }
}
